import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useState } from 'react';
import { useAppDispatch } from "@store/hooks";
import { createReading } from "../state/workspaceReadingsSlice";
import { uploadFile } from "@services/api";

interface CreateReadingModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

export function CreateReadingModal({ open, onClose, workspaceId }: CreateReadingModalProps) {
  const dispatch = useAppDispatch();

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');

  // Creation captures only what defines the reading — the document and its
  // name. Assignment metadata (description, instructions, due date,
  // required, who it's assigned to) lives in the separate assign flow.
  const [formData, setFormData] = useState({ title: '' });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setUploadedFile(file);
      if (!formData.title) {
        setFormData({ ...formData, title: file.name.replace('.pdf', '') });
      }
    }
  };

  const handleCreate = async () => {
    if (!uploadedFile || !formData.title) {
      setError('Please select a file and enter a title');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Step 1: Upload file to S3
      const uploadResult = await uploadFile(
        formData.title || uploadedFile.name,
        uploadedFile
      );

      if (!uploadResult.success) {
        throw new Error(uploadResult.error.message || 'File upload failed');
      }

      if (!uploadResult.data) {
        throw new Error('Document ID not returned from upload');
      }

      // Step 2: Create reading with document_id
      const payload = {
        workspace_id: workspaceId,
        document_id: uploadResult.data.documentId,
        reading_name: formData.title,
      };

      await dispatch(createReading(payload)).unwrap();

      // Success - close and reset
      onClose();
      setFormData({ title: '' });
      setUploadedFile(null);
      setError('');
    } catch (err: any) {
      console.error('Failed to create reading:', err);
      setError(err.message || 'Failed to create reading');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      onClose();
      setError('');
    }
  };

  const isValid = formData.title && uploadedFile;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Reading</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* File Upload */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <input
            accept="application/pdf"
            style={{ display: 'none' }}
            id="file-upload-input"
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor="file-upload-input">
            {/* color="inherit" follows text.primary (near-black on light,
                white on dark) so the outlined button keeps WCAG-AA contrast
                in both themes, unlike the default low-contrast primary. */}
            <Button
              variant="outlined"
              color="inherit"
              component="span"
              fullWidth
              disabled={uploading}
            >
              {uploadedFile ? `Selected: ${uploadedFile.name}` : 'Choose PDF File'}
            </Button>
          </label>
        </Box>

        {/* Reading Title */}
        <TextField
          fullWidth
          label="Reading Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          sx={{ mb: 2 }}
          required
          disabled={uploading}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isValid || uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : null}
        >
          {uploading ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
